import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  api,
  type ChartTaxonomyParent,
  type ChartUnitsBatchResult,
  type ChartUnitsBucket,
  type ChartUnitsSeries,
} from '../api/client.js';
import { integer, monthLabel } from '../format.js';
import {
  chunkKeys,
  drawableSeries,
  pathThrough,
  sparklineBatchPath,
  sparklineGeometry,
} from '../lib/sparkline.js';

/**
 * The Products cards' twelve-month units sparkline: a tiny inline SVG line, not a
 * Recharts chart, so the Products page never downloads the chart bundle for it.
 * The data is the charts endpoint's batched units series (the same buckets and
 * sale definition as the Charts page), fetched once per page of cards.
 */

const WIDTH = 112;
const HEIGHT = 28;
const PAD = 2;

export interface UnitSparklines {
  byKey: Map<string, ChartUnitsSeries>;
  isPending: boolean;
}

/**
 * The batched units series for a page of series or model cards under one parent:
 * one request per chunk of at most 50 keys (so one request for any real page),
 * never one per card. Returns the series by key once loaded.
 */
export function useUnitSparklines(
  scope: 'series' | 'model',
  parent: ChartTaxonomyParent,
  keys: readonly string[],
): UnitSparklines {
  const chunks = useMemo(() => chunkKeys(keys), [keys]);
  return useQueries({
    queries: chunks.map((chunk) => ({
      queryKey: ['charts', 'series-batch', scope, parent.category, parent.series ?? null, chunk],
      queryFn: () => api.get<ChartUnitsBatchResult>(sparklineBatchPath(scope, parent, chunk)),
      staleTime: 5 * 60_000,
    })),
    combine: (results) => {
      const byKey = new Map<string, ChartUnitsSeries>();
      for (const r of results) for (const s of r.data?.series ?? []) byKey.set(s.key, s);
      return { byKey, isPending: results.some((r) => r.isPending) };
    },
  });
}

/** A human reading of the buckets, for the tooltip and the screen reader. */
function describeBuckets(buckets: readonly ChartUnitsBucket[]): string {
  if (buckets.length === 0) return 'No monthly units';
  const first = buckets[0]!;
  const last = buckets[buckets.length - 1]!;
  const total = buckets.reduce((sum, b) => sum + b.unitsOrdered, 0);
  const range = `${monthLabel(first.start.slice(0, 7))} to ${monthLabel(last.start.slice(0, 7))}`;
  const values = buckets.map((b) => integer(b.unitsOrdered)).join(', ');
  const toDate = last.partial ? ' (the last month to date)' : '';
  return `Units per month, ${range}${toDate}: ${values}. Total ${integer(total)}.`;
}

/** The faint baseline every sparkline sits on. */
function Baseline({ stroke }: { stroke: string }) {
  return (
    <line
      x1={PAD}
      x2={WIDTH - PAD}
      y1={HEIGHT - PAD}
      y2={HEIGHT - PAD}
      stroke={stroke}
      strokeWidth={1}
    />
  );
}

/**
 * One sparkline. While the batch loads it holds the space with a faint baseline;
 * a missing series (an error, a key the batch did not return, or a stale key that
 * names no SKU) renders an em dash rather than a misleading flat line. A partial last bucket (the current
 * month to date) is drawn dashed, so a month still in progress never reads as a
 * collapse. Scaled to its own maximum: the line shows shape, and the figure beside
 * it carries the magnitude.
 */
export function Sparkline({
  series: entry,
  pending = false,
  label,
}: {
  series: ChartUnitsSeries | undefined;
  pending?: boolean;
  /** What the line is for (a series or model name), for the accessible name. */
  label: string;
}) {
  const series = drawableSeries(entry);
  if (!series) {
    return pending ? (
      <svg width={WIDTH} height={HEIGHT} aria-hidden data-testid="sparkline-pending">
        <Baseline stroke="var(--chart-grid)" />
      </svg>
    ) : (
      <span className="text-xs text-muted" title="No monthly units available">
        —
      </span>
    );
  }

  const values = series.buckets.map((b) => b.unitsOrdered);
  const { line, points } = sparklineGeometry(values, WIDTH, HEIGHT, PAD);
  const partialLast = points.length > 1 && series.buckets[series.buckets.length - 1]!.partial;
  const last = points[points.length - 1];
  const text = describeBuckets(series.buckets);

  return (
    <svg
      width={WIDTH}
      height={HEIGHT}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      role="img"
      aria-label={`${label}: ${text}`}
      data-testid="sparkline"
      data-values={values.join(',')}
      data-nonzero={values.filter((v) => v > 0).length}
      className="shrink-0 text-brand-600"
    >
      <title>{text}</title>
      <Baseline stroke="var(--chart-grid)" />
      {partialLast ? (
        <>
          {points.length > 2 && (
            <path
              d={pathThrough(points.slice(0, -1))}
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          )}
          <path
            d={pathThrough(points.slice(-2))}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeDasharray="2 2"
            strokeLinecap="round"
          />
        </>
      ) : (
        <path
          d={line}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      )}
      {last && <circle cx={last.x} cy={last.y} r={2} fill="currentColor" />}
    </svg>
  );
}
